import { getObjectId } from "./Utils"

export function showHiddenGem(user) {
    return [
        '5d663b175d39b511a08f99c3',
        '5d6634975d39b53d248f9980'
    ].includes(user?._id);
}

/**
 * Returns a person's full name
 *
 * @param firstName {String}
 * @param lastName {String}
 * @param [fallback] {String}
 * @return {String|undefined|*}
 */
export function getFullName(firstName, lastName, fallback) {
    return !firstName && !lastName ? fallback : `${firstName ? firstName : ""}${lastName ? " " + lastName : ""}`
}

/**
 * Check if the provided user email is verified
 * @param user {object}
 * @param user.emailList {object[]}
 * @param user.emailList[].email {string}
 * @param user.emailList[].verified {boolean}
 * @param email {string}
 * @returns {boolean}
 */
export function isUserEmailVerified(user, email) {
    const userEmailItem = (user?.emailList || []).find((emailItem) => emailItem.email === email)

    return userEmailItem?.verified === true
}

/**
 * Checks if the logged-in user's email is verified for a mission
 * @param user {object}
 * @param user._id {string}
 * @param user.emailList {object[]}
 * @param user.emailList[].email {string}
 * @param user.emailList[].verified {boolean}
 * @param mission {object}
 * @param mission.people {object[]}
 * @param mission.people[].ref {string|object}
 * @param mission.people[].email {string}
 * @returns {boolean}
 */
export function isUserEmailVerifiedForMission(user, mission) {
    const missionPerson = (mission?.people || []).find((missionPerson) => getObjectId(missionPerson.ref) === user._id)

    const userEmailItem = (user?.emailList || []).find((emailItem) => emailItem.email === missionPerson?.email)

    return userEmailItem?.verified === true
}

/**
 * Checks if the logged-in user's email is verified for a mission
 * @param user {object}
 * @param user._id {string}
 * @param user.emailList {object[]}
 * @param user.emailList[].email {string}
 * @param user.emailList[].verified {boolean}
 * @param org {object}
 * @param org.people {object[]}
 * @param org.people[].ref {string|object}
 * @param org.people[].email {string}
 * @returns {boolean}
 */
export function isUserEmailVerifiedForOrg(user, org) {
    const orgPerson = (org?.people || []).find((missionPerson) => getObjectId(missionPerson.ref) === user._id)

    const userEmailItem = (user?.emailList || []).find((emailItem) => emailItem.email === orgPerson?.email)

    return userEmailItem?.verified === true
}

const normalizeEmail = (email) => (typeof email === "string" ? email.trim().toLowerCase() : "")

/**
 * The email this person uses IN THIS ORG (the org-person's email, not the account's
 * primary) and whether it is verified - the address invites go out from and billing
 * mail goes to, so it is what gates both. A verified primary does not stand in for it.
 *
 * When the org email is the login email, person.emailVerified counts too, since older
 * accounts can be missing it from emailList. Falls back to the primary email (with its
 * own flag) for someone not on the org's people list yet. Mirrors
 * peopleHandler.isOrgEmailVerified() in the API.
 *
 * @param person {object} app.state.person
 * @param org {object} orgData
 * @returns {{email: string, verified: boolean}}
 */
export function getOrgEmailStatus(person, org) {
    const orgPerson = (org?.people || []).find((p) => getObjectId(p.ref) === person?._id)
    const email = orgPerson?.email || person?.email

    return { email, verified: isPersonEmailVerified(person, email) }
}

/**
 * Whether one of this person's own emails is verified - checked on the emailList entry,
 * or person.emailVerified when it is the login email. Case and spaces don't matter.
 *
 * @param person {object} app.state.person
 * @param email {string}
 * @returns {boolean}
 */
export function isPersonEmailVerified(person, email) {
    const target = normalizeEmail(email)

    const listed = (person?.emailList || []).find((item) => normalizeEmail(item.email) === target)
    const verified =
        listed?.verified === true || (normalizeEmail(person?.email) === target && person?.emailVerified === true)

    return Boolean(target) && verified
}
